import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import client from 'utils/client';
import AppDataGrid from 'components/AppDataGrid/AppDataGrid';
import { ActivePill, TransactionStatusPill, SettledPill } from 'components/StatusPill/StatusPill';
import { formatCurrency, formatThousand } from 'utils/helpers';
import { useToast } from 'components/Toast/useToast';
import './merchant-detail.css';

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Today' },
  { value: 'weekly', label: 'This week' },
  { value: 'monthly', label: 'This month' },
  { value: '3months', label: 'Last 3 months' },
  { value: '6months', label: 'Last 6 months' },
  { value: '1year', label: 'This year' },
];

const PERIOD_LABEL = {
  daily: 'today',
  weekly: 'this week',
  monthly: 'this month',
  '3months': 'last 3 months',
  '6months': 'last 6 months',
  '1year': 'this year',
};

const BRAND = '#1e3a8a';
const WARN = '#b45309';

const initialsOf = (first, last) => `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || '?';

function StatCard({ label, value, sub, tone }) {
  return (
    <div className={`md-stat ${tone ? `md-stat--${tone}` : ''}`}>
      <div className='md-stat-label'>{label}</div>
      <div className='md-stat-value tabular'>{value}</div>
      {sub ? <div className='md-stat-sub'>{sub}</div> : null}
    </div>
  );
}

function MerchantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { onOpen } = useToast();
  const [merchant, setMerchant] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [portalEmail, setPortalEmail] = useState(null);

  const enablePortal = () => client
    .post(`/merchants/${id}/portal-access`)
    .then((res) => { setTempPassword(res.data.data.password); setPortalEmail(res.data.data.email); })
    .catch((e) => onOpen({ type: 'error', message: e?.message || 'Failed to enable portal access' }));

  const revokePortal = () => client
    .delete(`/merchants/${id}/portal-access`)
    .then(() => onOpen({ type: 'success', message: 'Portal access revoked' }))
    .catch((e) => onOpen({ type: 'error', message: e?.message || 'Failed to revoke access' }));

  useEffect(() => {
    client.get(`/merchants/${id}`)
      .then((res) => setMerchant(res.data.data))
      .catch((err) => onOpen({ type: 'error', message: err?.message || 'Failed to load merchant' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setLoading(true);
    client.get(`/merchants/${id}/metrics?period=${period}`)
      .then((res) => setMetrics(res.data.data))
      .catch((err) => onOpen({ type: 'error', message: err?.message || 'Failed to load metrics' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, period]);

  const totals = metrics?.totals;
  const daily = metrics?.daily || [];
  const terminals = metrics?.terminals || [];
  const recent = metrics?.recentTransactions || [];
  const periodLabel = PERIOD_LABEL[period] || '';

  const name = merchant ? `${merchant.first_name || ''} ${merchant.last_name || ''}`.trim() : '';

  const recentColumns = [
    {
      field: 'reference',
      headerName: 'Reference',
      width: 260,
      renderCell: (p) => <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{p.row.reference}</span>,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => <TransactionStatusPill code={p.row.status} />,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (p) => formatCurrency(p.row.amount),
    },
    {
      field: 'transaction_date',
      headerName: 'Date',
      width: 180,
      valueGetter: (p) => dayjs(p.row.transaction_date || p.row.created_at).format('MMM DD, YYYY h:mma'),
    },
    {
      field: 'terminal',
      headerName: 'Terminal',
      width: 140,
      renderCell: (p) => (
        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
          {p.row.terminal?.terminal_id || '—'}
        </span>
      ),
    },
    {
      field: 'settled',
      headerName: 'Settled',
      width: 100,
      renderCell: (p) => <SettledPill settled={!!p.row.settled} />,
    },
  ];

  const terminalColumns = [
    {
      field: 'terminalId',
      headerName: 'Terminal ID',
      width: 140,
      renderCell: (p) => (
        <Link to={`/terminals/${p.row.id}`} className='md-link' style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
          {p.row.terminalId || `#${p.row.id}`}
        </Link>
      ),
    },
    { field: 'serialNumber', headerName: 'Serial', width: 140, cellClassName: 'cell-mono' },
    { field: 'location', headerName: 'Location', width: 160 },
    {
      field: 'active',
      headerName: 'Status',
      width: 110,
      renderCell: (p) => <ActivePill active={p.row.active} />,
    },
    {
      field: 'count',
      headerName: `Txns ${periodLabel}`,
      width: 160,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (p) => formatThousand(p.row.count),
    },
    {
      field: 'amount',
      headerName: 'Volume',
      width: 160,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (p) => formatCurrency(p.row.amount),
    },
    {
      field: 'settledAmount',
      headerName: 'Settled',
      width: 160,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (p) => formatCurrency(p.row.settledAmount),
    },
  ];

  return (
    <div className='md-page'>
      {tempPassword ? (
        <div className='md-portal-dialog' role='dialog' aria-modal='true'>
          <div className='md-portal-dialog-card'>
            <h2 className='md-card-title'>Temporary password</h2>
            <p className='md-card-hint'>
              {portalEmail ? <>Login email: <strong>{portalEmail}</strong>. </> : null}
              This login covers every branch that shares this email. Copy the password now — it is shown only once, and the merchant must change it on first login.
            </p>
            <code className='md-portal-password'>{tempPassword}</code>
            <div className='md-header-right' style={{ marginTop: 16 }}>
              <button
                type='button'
                className='btn-secondary'
                onClick={() => { navigator.clipboard?.writeText(tempPassword); onOpen({ message: 'Copied' }); }}>
                Copy
              </button>
              <button type='button' className='btn-primary' onClick={() => setTempPassword(null)}>Done</button>
            </div>
          </div>
        </div>
      ) : null}

      <div className='md-breadcrumbs'>
        <Link to='/merchants' className='md-link'>Merchants</Link>
        <span>/</span>
        <span>{name || `#${id}`}</span>
      </div>

      <div className='md-header'>
        <div className='md-header-left'>
          <span className='md-avatar' aria-hidden='true'>{initialsOf(merchant?.first_name, merchant?.last_name)}</span>
          <div>
            <h1 className='md-name'>{name || '—'}</h1>
            <div className='md-subline'>
              <span>{merchant?.email || '—'}</span>
              {merchant?.phone_number ? <span>· {merchant.phone_number}</span> : null}
              {merchant ? <ActivePill active={merchant.active} /> : null}
            </div>
          </div>
        </div>
        <div className='md-header-right'>
          <button
            type='button'
            className='btn-secondary'
            onClick={enablePortal}
            disabled={!merchant}>
            Enable / Reset portal access
          </button>
          <button
            type='button'
            className='btn-secondary'
            onClick={revokePortal}
            disabled={!merchant}>
            Revoke access
          </button>
          <button
            type='button'
            className='btn-secondary'
            onClick={() => navigate(`/edit-merchant/${id}`, { state: { merchant } })}
            disabled={!merchant}>
            Edit
          </button>
        </div>
      </div>

      <div className='md-toolbar'>
        <div className='md-period-pill'>
          {PERIOD_OPTIONS.map((o) => (
            <button
              key={o.value}
              type='button'
              className={`md-period-option ${period === o.value ? 'is-active' : ''}`}
              onClick={() => setPeriod(o.value)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className='md-stats-row'>
        <StatCard
          label={`Settled ${periodLabel}`}
          value={formatCurrency(totals?.settledAmount || 0)}
          sub={`${formatThousand(totals?.settledCount || 0)} transactions`}
          tone='success'
        />
        <StatCard
          label='Pending settlement'
          value={formatCurrency(totals?.pendingAmount || 0)}
          sub={`${formatThousand(totals?.pendingCount || 0)} transactions`}
          tone='warning'
        />
        <StatCard
          label={`Total volume ${periodLabel}`}
          value={formatCurrency(totals?.txnAmount || 0)}
          sub={`${formatThousand(totals?.txnCount || 0)} transactions`}
        />
      </div>

      <div className='md-card'>
        <div className='md-card-head'>
          <div className='md-card-title'>Settled per day</div>
          <div className='md-card-hint'>Some merchants settle in bulk at EOD — pending rises during the day, then clears.</div>
        </div>
        <div style={{ height: 260 }}>
          {loading ? (
            <div className='md-loading'>Loading…</div>
          ) : daily.length === 0 ? (
            <div className='md-empty'>No transactions in this period.</div>
          ) : (
            <ResponsiveContainer>
              <LineChart data={daily} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray='3 3' stroke='#e2e8f0' />
                <XAxis
                  dataKey='bucket'
                  tickFormatter={(v) => dayjs(v).format('MMM D')}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                />
                <YAxis
                  tickFormatter={(v) => formatThousand(v)}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  width={70}
                />
                <Tooltip
                  formatter={(v) => formatCurrency(v)}
                  labelFormatter={(v) => dayjs(v).format('MMM D, YYYY')}
                />
                <Line type='monotone' dataKey='settledAmount' name='Settled' stroke={BRAND} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type='monotone' dataKey='pendingAmount' name='Pending' stroke={WARN} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className='md-card'>
        <div className='md-card-head'>
          <div className='md-card-title'>Terminals</div>
          <div className='md-card-hint'>Volume per terminal {periodLabel}.</div>
        </div>
        <AppDataGrid
          className='md-grid'
          rows={terminals}
          columns={terminalColumns}
          loading={loading}
          height={terminals.length ? Math.min(60 + terminals.length * 52, 480) : 200}
          hideFooter={terminals.length <= 10}
          disableColumnFilter
        />
      </div>

      <div className='md-card'>
        <div className='md-card-head'>
          <div className='md-card-title'>Recent transactions</div>
          <div className='md-card-hint'>Last 10 transactions for this merchant.</div>
        </div>
        <AppDataGrid
          className='md-grid'
          rows={recent}
          columns={recentColumns}
          loading={loading}
          height={Math.min(60 + (recent.length || 1) * 52, 580)}
          hideFooter
          disableColumnFilter
        />
      </div>
    </div>
  );
}

export default MerchantDetail;
